import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { picassoApi, PicassoId, TurnInput } from '../api/picassoApi'

export const errorMessage = (e: unknown) => {
  const err = e as { response?: { data?: { message?: string; detail?: string } }; message?: string }
  return err?.response?.data?.message || err?.response?.data?.detail || err?.message || 'Something went wrong'
}

export function usePicassoTools() {
  return useQuery({ queryKey: ['picasso-tools'], queryFn: picassoApi.getTools, staleTime: Infinity })
}

export function usePicassoSessions(params: { mine?: boolean; limit?: number; offset?: number }) {
  return useQuery({
    queryKey: ['picasso-sessions', params],
    queryFn: () => picassoApi.getSessions(params),
    placeholderData: keepPreviousData,
  })
}

export function usePicassoSession(id: PicassoId | undefined) {
  return useQuery({
    queryKey: ['picasso-session', String(id)],
    queryFn: () => picassoApi.getSession(id!),
    enabled: !!id,
  })
}

export function useCreatePicassoSession() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (title?: string) => picassoApi.createSession(title),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['picasso-sessions'] }),
  })
}

export function useRenamePicassoSession(id: PicassoId) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (title: string) => picassoApi.renameSession(id, title),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['picasso-session', String(id)] })
      qc.invalidateQueries({ queryKey: ['picasso-sessions'] })
    },
  })
}

export function useDeletePicassoSession() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (id: PicassoId) => picassoApi.deleteSession(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['picasso-sessions'] }),
    onError: (e: Error) => toast({ title: 'Could not delete', description: errorMessage(e), variant: 'destructive' }),
  })
}

/** Run a turn; the session's turn list is updated in place, so the answer shows as soon as it lands. */
export function useRunTurn() {
  const qc = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: ({ id, body }: { id: PicassoId; body: TurnInput }) => picassoApi.runTurn(id, body),
    onSuccess: (turn, { id }) => {
      qc.invalidateQueries({ queryKey: ['picasso-session', String(id)] })
      qc.invalidateQueries({ queryKey: ['picasso-sessions'] })
      if (turn.error) toast({ title: 'The concierge returned an error', description: turn.error, variant: 'destructive' })
    },
    onError: (e: Error) => toast({ title: 'Could not reach the concierge', description: errorMessage(e), variant: 'destructive' }),
  })
}

export function useShopperSearches(params: { limit?: number; client?: string; session?: string }) {
  return useQuery({
    queryKey: ['picasso-shopper', params],
    queryFn: () => picassoApi.getShopperSearches(params),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  })
}

export function useShopperSearch(searchId: string | undefined) {
  return useQuery({
    queryKey: ['picasso-shopper-search', searchId],
    queryFn: () => picassoApi.getShopperSearch(searchId!),
    enabled: !!searchId,
  })
}
